import { siteTrafficTag, textUnlessProblem } from './baseApi'
import { operatorApi } from './operatorApi'

// Cache identity for the operator surface, layered over the generated
// client for the reason `authoring.ts` is: the generated file is
// overwritten on every change to the contract.
//
// A write to one tenant's account answers with the whole account, so its
// own screen redraws from the response. What it invalidates is everything
// *derived* from that account elsewhere — the tenant list's balance
// column, the cross-tenant overview — which is why nearly every write
// here names those two.

const tenantTag = (tenant: string) => ({ type: 'OperatorTenant' as const, id: tenant })
const billingTag = (tenant: string) => ({ type: 'Billing' as const, id: tenant })
const administratorsTag = (tenant: string) => ({ type: 'Administrators' as const, id: tenant })
const domainsTag = (tenant: string) => ({ type: 'Domains' as const, id: tenant })
// The authoring surface's listing of the same grants, which an operator
// who also administers the tenant may be holding.
const tenantDomainsTag = (tenant: string) => ({ type: 'TenantDomains' as const, id: tenant })
const derived = ['OperatorTenants', 'BillingOverview'] as const
const tenantTrafficTag = (tenant: string) => ({ type: 'TenantTraffic' as const, id: tenant })
// The authoring surface's report for one site (`siteTrafficTag`), which
// an operator who also administers the tenant may have open: a bind or an
// unbind changes the state its card draws, and a collection the days.

export const operator = operatorApi.enhanceEndpoints({
  endpoints: {
    // YAML, which the generated query would read as JSON; restated with
    // the response handler it lacks, and not kept after the download.
    getOperatorOpenApiyaml: {
      query: () => ({ url: `/v1/operator/openapi.yaml`, responseHandler: textUnlessProblem }),
      keepUnusedDataFor: 0,
    },
    listOperatorTenants: { providesTags: ['OperatorTenants'] },
    createTenant: { invalidatesTags: [...derived, 'Tenants'] },
    getOperatorTenant: { providesTags: (_result, _error, arg) => [tenantTag(arg.tenant)] },
    updateTenant: { invalidatesTags: (_result, _error, arg) => [tenantTag(arg.tenant), 'OperatorTenants'] },
    // Offboarding withdraws the caller's own membership too, when they held
    // one, so the authoring tenant list is stale after it.
    offboardTenant: {
      invalidatesTags: (_result, _error, arg) => [tenantTag(arg.tenant), billingTag(arg.tenant), ...derived, 'Tenants'],
    },
    retryPurge: { invalidatesTags: (_result, _error, arg) => [tenantTag(arg.tenant)] },
    // A reinstated tenant is listed as active again and its account can
    // take an agreement, so the listing, the overview and its billing
    // face are all stale after it.
    reinstateTenant: {
      invalidatesTags: (_result, _error, arg) => [tenantTag(arg.tenant), billingTag(arg.tenant), ...derived],
    },

    listAdministrators: { providesTags: (_result, _error, arg) => [administratorsTag(arg.tenant)] },
    addAdministrator: {
      invalidatesTags: (_result, _error, arg) => [administratorsTag(arg.tenant), tenantTag(arg.tenant), 'Tenants', 'OperatorTenants'],
    },
    removeAdministrator: {
      invalidatesTags: (_result, _error, arg) => [administratorsTag(arg.tenant), tenantTag(arg.tenant), 'Tenants', 'OperatorTenants'],
    },
    // The listing says whose two-step verification is on, and the reset
    // logs a line on the tenant. No membership moves, so nothing else is.
    resetAdministratorMfa: {
      invalidatesTags: (_result, _error, arg) => [administratorsTag(arg.tenant), tenantTag(arg.tenant)],
    },

    listDomains: { providesTags: (_result, _error, arg) => [domainsTag(arg.tenant)] },
    // A grant and a revoke each log a line on the tenant, so the overview
    // is stale after either.
    grantDomain: {
      invalidatesTags: (_result, _error, arg) => [domainsTag(arg.tenant), tenantDomainsTag(arg.tenant), tenantTag(arg.tenant)],
    },
    revokeDomain: {
      invalidatesTags: (_result, _error, arg) => [domainsTag(arg.tenant), tenantDomainsTag(arg.tenant), tenantTag(arg.tenant)],
    },

    // A bind and an unbind each log a line on the tenant, and move the
    // customer's card. Collect now changes nothing until the run has
    // gone, which the screen waits for by re-reading the list itself, and
    // it invalidates the card when it sees the run arrive.
    listTenantTraffic: { providesTags: (_result, _error, arg) => [tenantTrafficTag(arg.tenant)] },
    bindTenantTraffic: {
      invalidatesTags: (_result, _error, arg) => [
        tenantTrafficTag(arg.tenant),
        siteTrafficTag(arg.tenant, arg.website),
        tenantTag(arg.tenant),
        // The run a bind starts checks its connection first.
        'TrafficConnections',
      ],
    },
    unbindTenantTraffic: {
      invalidatesTags: (_result, _error, arg) => [
        tenantTrafficTag(arg.tenant),
        siteTrafficTag(arg.tenant, arg.website),
        tenantTag(arg.tenant),
      ],
    },
    collectTenantTraffic: { invalidatesTags: (_result, _error, arg) => [tenantTrafficTag(arg.tenant), 'TrafficConnections'] },
    listTrafficSources: { keepUnusedDataFor: 300 },
    listTrafficConnections: { providesTags: ['TrafficConnections'] },

    getTenantBilling: { providesTags: (_result, _error, arg) => [billingTag(arg.tenant)] },
    addAgreement: { invalidatesTags: (_result, _error, arg) => [billingTag(arg.tenant), tenantTag(arg.tenant), ...derived] },
    // The list says which tenants are billed, and marking one not billed
    // ends its agreement, which the overview counts.
    setTenantBilled: { invalidatesTags: (_result, _error, arg) => [billingTag(arg.tenant), tenantTag(arg.tenant), ...derived] },
    recordPayment: { invalidatesTags: (_result, _error, arg) => [billingTag(arg.tenant), tenantTag(arg.tenant), ...derived] },
    voidPayment: { invalidatesTags: (_result, _error, arg) => [billingTag(arg.tenant), tenantTag(arg.tenant), ...derived] },
    addAdjustment: { invalidatesTags: (_result, _error, arg) => [billingTag(arg.tenant), tenantTag(arg.tenant), ...derived] },
    voidAdjustment: { invalidatesTags: (_result, _error, arg) => [billingTag(arg.tenant), tenantTag(arg.tenant), ...derived] },
    setPeriodNote: { invalidatesTags: (_result, _error, arg) => [billingTag(arg.tenant)] },

    listPlans: { providesTags: ['Plans'] },
    createPlan: { invalidatesTags: ['Plans'] },
    updatePlan: { invalidatesTags: ['Plans'] },

    getBillingOverview: { providesTags: ['BillingOverview'] },
    // The ledger is fetched for an export and read once; it is not kept.
    getLedger: { keepUnusedDataFor: 0 },
  },
})

export const {
  useListOperatorTenantsQuery,
  useCreateTenantMutation,
  useGetOperatorTenantQuery,
  useUpdateTenantMutation,
  useOffboardTenantMutation,
  useRetryPurgeMutation,
  useReinstateTenantMutation,
  useListAdministratorsQuery,
  useAddAdministratorMutation,
  useRemoveAdministratorMutation,
  useResetAdministratorMfaMutation,
  useListDomainsQuery,
  useGrantDomainMutation,
  useRevokeDomainMutation,
  useGetTenantBillingQuery,
  useAddAgreementMutation,
  useSetTenantBilledMutation,
  useRecordPaymentMutation,
  useVoidPaymentMutation,
  useAddAdjustmentMutation,
  useVoidAdjustmentMutation,
  useSetPeriodNoteMutation,
  useListPlansQuery,
  useCreatePlanMutation,
  useUpdatePlanMutation,
  useGetBillingOverviewQuery,
  useLazyGetLedgerQuery,
  useLazyGetOperatorOpenApiyamlQuery,
  useListTenantTrafficQuery,
  useBindTenantTrafficMutation,
  useUnbindTenantTrafficMutation,
  useCollectTenantTrafficMutation,
  useListTrafficSourcesQuery,
  useListTrafficConnectionsQuery,
} = operator

export type {
  Agreement,
  Billing,
  BillingOverview,
  DomainGrant,
  LogEntry,
  Plan,
  StatementLine,
  Tenant as OperatedTenant,
  TenantStatus,
  TenantSummary,
  TenantTraffic,
  TrafficBinding,
  TrafficConnection,
  TrafficSite,
  TrafficSource,
  TrafficStatus,
} from './operatorApi'
